/**
 * Permission Store
 * Centralized store for all permission-related state
 * Single source of truth for route guards and access control
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
export interface PermissionState {
  // Loading states
  permissionsReady: boolean
  accountStatusLoading: boolean
  companyPermissionsLoading: boolean
  
  // Account status permissions
  canCreateRooms: boolean
  canJoinRooms: boolean
  canJoinCustomRooms: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  canAccessBilling: boolean
  canAccessProfile: boolean
  hasFullAccess: boolean
  
  // Admin permissions (elevated admin flags come from GET /api/admin/capabilities, not client caches)
  isGlobalAdmin: boolean
  isPlatformAdmin: boolean
  /** True once server attested (or anonymous user — no session to verify). */
  adminCapabilitiesVerified: boolean
  adminCapabilitiesLoading: boolean
  
  // Company permissions
  isCompanyManagerOrAdmin: boolean
  hasCompanyAccess: boolean
  currentCompany: any | null
  isCompanyDisabled: boolean
  
  // Account status info
  subscriptionStatus: string
  paymentStatus: string
  restrictions: string[]
  statusMessage: string
  needsAction: boolean
  actionType: 'payment' | 'subscription' | 'none'
  actionMessage: string
  
  // Computed flags
  allowCompanyAccessWhenDisabled: boolean
  
  // Actions
  setAccountStatus: (status: Partial<AccountStatusData>) => void
  setCompanyPermissions: (permissions: Partial<CompanyPermissionData>) => void
  setPlatformAdmin: (isAdmin: boolean) => void
  beginAdminCapabilitiesFetch: () => void
  applyServerAdminCapabilities: (input: {
    meetsGlobalAdminRouteGate: boolean
    meetsAdminAccessRouteGate: boolean
  }) => void
  /** Logged-out / no session: skip HTTP, mark verified so non-admin routes are not blocked. */
  setAnonymousAdminCapabilities: () => void
  resetAdminSessionCapabilities: () => void
  applyDevForceAdminOverride: () => void
  refreshPermissions: () => Promise<void>
  checkPermission: (resource: string, action: string) => boolean
}

interface AccountStatusData {
  loading: boolean
  canCreateRooms: boolean
  canJoinRooms: boolean
  canJoinCustomRooms: boolean
  canAccessDashboard: boolean
  canAccessCompanyManagement: boolean
  canAccessBilling: boolean
  canAccessProfile: boolean
  hasFullAccess: boolean
  isGlobalAdmin: boolean
  subscriptionStatus: string
  paymentStatus: string
  restrictions: string[]
  statusMessage: string
  needsAction: boolean
  actionType: 'payment' | 'subscription' | 'none'
  actionMessage: string
}

interface CompanyPermissionData {
  loading: boolean
  isGlobalAdmin: boolean
  isCompanyManagerOrAdmin: boolean
  hasCompanyAccess: boolean
  currentCompany: any | null
  isCompanyDisabled: boolean
  canAccessCompanyManagement: boolean
}

function computeAllowCompanyAccessWhenDisabled(input: {
  canAccessCompanyManagement: boolean
  isCompanyManagerOrAdmin: boolean
}): boolean {
  return !input.canAccessCompanyManagement && !input.isCompanyManagerOrAdmin
}

const isDevForceAdmin = (): boolean =>
  import.meta.env.DEV &&
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('forceAdmin') === 'true'

export const usePermissionStore = create<PermissionState>()(
  subscribeWithSelector((set, get) => ({
    // Initial loading states
    permissionsReady: false,
    accountStatusLoading: true,
    companyPermissionsLoading: true,
    
    // Initial permission states
    canCreateRooms: false,
    canJoinRooms: false,
    canJoinCustomRooms: false,
    canAccessDashboard: false,
    canAccessCompanyManagement: false,
    canAccessBilling: false,
    canAccessProfile: true, // Profile is always accessible
    hasFullAccess: false,
    
    // Initial admin states
    isGlobalAdmin: false,
    isPlatformAdmin: false,
    adminCapabilitiesVerified: false,
    adminCapabilitiesLoading: false,
    
    // Initial company states
    isCompanyManagerOrAdmin: false,
    hasCompanyAccess: false,
    currentCompany: null,
    isCompanyDisabled: false,
    
    // Initial account status
    subscriptionStatus: 'none',
    paymentStatus: 'none',
    restrictions: [],
    statusMessage: 'Loading...',
    needsAction: false,
    actionType: 'none',
    actionMessage: '',
    
    // Computed
    allowCompanyAccessWhenDisabled: false,
    
    // Set account status data
    setAccountStatus: (status: Partial<AccountStatusData>) => {
      const current = get()
      const newState: Partial<PermissionState> = {}
      const nextCanAccessCompanyManagement = 'canAccessCompanyManagement' in status
        ? (status.canAccessCompanyManagement ?? false)
        : current.canAccessCompanyManagement
      const nextIsCompanyManagerOrAdmin = current.isCompanyManagerOrAdmin
      
      if ('loading' in status) {
        newState.accountStatusLoading = status.loading
      }
      
      if ('canCreateRooms' in status) {
        newState.canCreateRooms = status.canCreateRooms ?? false
      }
      if ('canJoinRooms' in status) {
        newState.canJoinRooms = status.canJoinRooms ?? false
      }
      if ('canJoinCustomRooms' in status) {
        newState.canJoinCustomRooms = status.canJoinCustomRooms ?? false
      }
      if ('canAccessDashboard' in status) {
        newState.canAccessDashboard = status.canAccessDashboard ?? false
      }
      if ('canAccessCompanyManagement' in status) {
        newState.canAccessCompanyManagement = nextCanAccessCompanyManagement
      }
      if ('canAccessBilling' in status) {
        newState.canAccessBilling = status.canAccessBilling ?? false
      }
      if ('canAccessProfile' in status) {
        newState.canAccessProfile = status.canAccessProfile ?? true
      }
      if ('hasFullAccess' in status) {
        newState.hasFullAccess = status.hasFullAccess ?? false
      }
      // isGlobalAdmin is server-attested only (ignore cloud/account hints here)
      if ('subscriptionStatus' in status) {
        newState.subscriptionStatus = status.subscriptionStatus ?? 'none'
      }
      if ('paymentStatus' in status) {
        newState.paymentStatus = status.paymentStatus ?? 'none'
      }
      if ('restrictions' in status) {
        newState.restrictions = status.restrictions ?? []
      }
      if ('statusMessage' in status) {
        newState.statusMessage = status.statusMessage ?? ''
      }
      if ('needsAction' in status) {
        newState.needsAction = status.needsAction ?? false
      }
      if ('actionType' in status) {
        newState.actionType = status.actionType ?? 'none'
      }
      if ('actionMessage' in status) {
        newState.actionMessage = status.actionMessage ?? ''
      }
      
      // Update permissions ready state
      const accountLoading = 'loading' in status ? status.loading : current.accountStatusLoading
      const companyLoading = current.companyPermissionsLoading
      newState.permissionsReady = !accountLoading && !companyLoading
      
      // Update computed flags
      newState.allowCompanyAccessWhenDisabled = computeAllowCompanyAccessWhenDisabled({
        canAccessCompanyManagement: nextCanAccessCompanyManagement,
        isCompanyManagerOrAdmin: nextIsCompanyManagerOrAdmin,
      })
      
      set(newState)
    },
    
    // Set company permissions data
    setCompanyPermissions: (permissions: Partial<CompanyPermissionData>) => {
      const current = get()
      const newState: Partial<PermissionState> = {}
      const nextCanAccessCompanyManagement = 'canAccessCompanyManagement' in permissions
        ? (permissions.canAccessCompanyManagement ?? false)
        : current.canAccessCompanyManagement
      const nextIsCompanyManagerOrAdmin = 'isCompanyManagerOrAdmin' in permissions
        ? (permissions.isCompanyManagerOrAdmin ?? false)
        : current.isCompanyManagerOrAdmin
      
      if ('loading' in permissions) {
        newState.companyPermissionsLoading = permissions.loading
      }
      
      // isGlobalAdmin / elevated flags: server capabilities only
      if ('isCompanyManagerOrAdmin' in permissions) {
        newState.isCompanyManagerOrAdmin = nextIsCompanyManagerOrAdmin
      }
      if ('hasCompanyAccess' in permissions) {
        newState.hasCompanyAccess = permissions.hasCompanyAccess ?? false
      }
      if ('currentCompany' in permissions) {
        newState.currentCompany = permissions.currentCompany ?? null
      }
      if ('isCompanyDisabled' in permissions) {
        newState.isCompanyDisabled = permissions.isCompanyDisabled ?? false
      }
      if ('canAccessCompanyManagement' in permissions) {
        newState.canAccessCompanyManagement = nextCanAccessCompanyManagement
      }
      
      // Update permissions ready state
      const accountLoading = current.accountStatusLoading
      const companyLoading = 'loading' in permissions ? permissions.loading : current.companyPermissionsLoading
      newState.permissionsReady = !accountLoading && !companyLoading
      
      // Update computed flags
      newState.allowCompanyAccessWhenDisabled = computeAllowCompanyAccessWhenDisabled({
        canAccessCompanyManagement: nextCanAccessCompanyManagement,
        isCompanyManagerOrAdmin: nextIsCompanyManagerOrAdmin,
      })
      
      set(newState)
    },
    
    // Set platform admin status (legacy callers; prefer applyServerAdminCapabilities)
    setPlatformAdmin: (isAdmin: boolean) => {
      set({ isPlatformAdmin: isAdmin })
    },

    beginAdminCapabilitiesFetch: () => {
      set({
        adminCapabilitiesLoading: true,
        adminCapabilitiesVerified: false,
        isGlobalAdmin: false,
        isPlatformAdmin: false,
      })
    },

    applyServerAdminCapabilities: (input) => {
      set({
        isGlobalAdmin: input.meetsGlobalAdminRouteGate,
        isPlatformAdmin: input.meetsAdminAccessRouteGate,
        adminCapabilitiesVerified: true,
        adminCapabilitiesLoading: false,
      })
      get().applyDevForceAdminOverride()
    },

    setAnonymousAdminCapabilities: () => {
      set({
        isGlobalAdmin: false,
        isPlatformAdmin: false,
        adminCapabilitiesVerified: true,
        adminCapabilitiesLoading: false,
      })
    },

    resetAdminSessionCapabilities: () => {
      set({
        isGlobalAdmin: false,
        isPlatformAdmin: false,
        adminCapabilitiesVerified: false,
        adminCapabilitiesLoading: false,
      })
    },

    applyDevForceAdminOverride: () => {
      if (!isDevForceAdmin()) return
      set({ isGlobalAdmin: true, isPlatformAdmin: true })
    },
    
    // Refresh permissions (triggers external hooks to refresh)
    refreshPermissions: async () => {
      // This will be called by components that use the hooks
      // The hooks will then call setAccountStatus/setCompanyPermissions
      console.log('[PermissionStore] Refresh requested - components should refresh their hooks')
    },
    
    // Check permission for a resource/action
    checkPermission: (resource: string, action: string): boolean => {
      const state = get()
      
      if (state.isGlobalAdmin || state.isPlatformAdmin) {
        return true
      }
      
      // Resource-specific checks
      switch (resource) {
        case 'rooms':
          if (action === 'create') return state.canCreateRooms
          if (action === 'join') return state.canJoinRooms
          if (action === 'join_custom') return state.canJoinCustomRooms
          return false
          
        case 'dashboard':
          return state.canAccessDashboard
          
        case 'company':
          if (action === 'manage') return state.canAccessCompanyManagement
          if (action === 'access') return state.hasCompanyAccess || state.canAccessCompanyManagement
          return false
          
        case 'billing':
          return state.canAccessBilling
          
        case 'profile':
          return state.canAccessProfile
          
        default:
          return false
      }
    },
  }))
)
